import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  LayoutDashboard,
  Users,
  FileText,
  Image,
  MessageSquare,
  Phone,
  LogOut,
  Menu,
  X,
  ChevronRight,
  GraduationCap,
  TrendingUp,
  Download,
  Eye,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Trash2,
  UserCog,
  Lock,
  Pencil,
  FileDown
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar
} from 'recharts';
import { API_URL, formatDate, centerNames, centerColors } from '../../lib/utils';
import { authFetch, getCurrentUser, logout } from '../../lib/auth';
import { toast } from 'sonner';

const allNavItems = [
  { icon: LayoutDashboard, label: 'Tableau de bord', path: '/admin/dashboard', section: 'dashboard' },
  { icon: Users, label: 'Preinscriptions', path: '/admin/preregistrations', section: 'preregistrations' },
  { icon: Phone, label: 'Rappels', path: '/admin/callbacks', section: 'callbacks' },
  { icon: Pencil, label: 'Contenu', path: '/admin/content', section: 'blog' },
  { icon: FileText, label: 'Blog', path: '/admin/blog', section: 'blog' },
  { icon: Image, label: 'Medias', path: '/admin/media', section: 'media' },
  { icon: MessageSquare, label: 'Messages', path: '/admin/messages', section: 'messages' },
  { icon: UserCog, label: 'Utilisateurs', path: '/admin/users', section: 'users' },
];

const ROLE_PERMISSIONS = {
  directeur: ['dashboard', 'preregistrations', 'callbacks', 'blog', 'media', 'messages', 'users'],
  marketing: ['dashboard', 'blog', 'media'],
  commercial: ['dashboard', 'preregistrations', 'callbacks', 'messages'],
};

const ROLE_LABELS = { directeur: 'Directeur', marketing: 'Marketing', commercial: 'Commercial' };

const COLORS = ['#003366', '#DC143C', '#FFD700', '#16a34a', '#8B5CF6'];

export const AdminLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [pwdForm, setPwdForm] = useState({ current_password: '', new_password: '' });
  const [pwdLoading, setPwdLoading] = useState(false);

  const currentUser = getCurrentUser();
  const userRole = currentUser.role || 'commercial';
  const permissions = ROLE_PERMISSIONS[userRole] || [];
  const navItems = allNavItems.filter(item => permissions.includes(item.section));

  const handleLogout = async () => {
    await logout();
    navigate('/admin');
    toast.success('Deconnexion reussie');
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPwdLoading(true);
    try {
      const res = await authFetch(`${API_URL}/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(pwdForm),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      toast.success('Mot de passe modifie');
      setShowPasswordModal(false);
      setPwdForm({ current_password: '', new_password: '' });
    } catch (err) {
      toast.error(err.message || 'Erreur');
    } finally { setPwdLoading(false); }
  };

  useEffect(() => {
    const user = getCurrentUser();
    if (!user.role) {
      navigate('/admin');
    }
  }, [navigate]);

  return (
    <div className="min-h-screen bg-gray-100 flex" data-testid="admin-layout">
      {/* Sidebar */}
      <aside className={`
        fixed inset-y-0 left-0 z-50 w-64 bg-[#003366] transform transition-transform duration-300
        lg:translate-x-0 lg:static
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-white/10">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                <GraduationCap className="text-[#003366]" size={24} />
              </div>
              <div>
                <span className="font-heading text-lg font-bold text-white block leading-tight">CEFAT</span>
                <span className="text-xs text-white/60">Administration</span>
              </div>
            </Link>
          </div>

          {/* User info */}
          <div className="px-4 py-3 border-b border-white/10">
            <p className="text-white text-sm font-medium truncate">{currentUser.name || 'Admin'}</p>
            <p className="text-white/50 text-xs">{ROLE_LABELS[userRole] || userRole}</p>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`admin-nav-item ${location.pathname === item.path ? 'active bg-white/10 text-white' : ''}`}
                data-testid={`admin-nav-${item.path.split('/').pop()}`}
              >
                <item.icon size={20} />
                {item.label}
              </Link>
            ))}
          </nav>

          {/* Bottom actions */}
          <div className="p-4 border-t border-white/10 space-y-2">
            <button
              onClick={() => setShowPasswordModal(true)}
              className="admin-nav-item w-full text-white/70 hover:text-white hover:bg-white/10"
              data-testid="change-password-btn"
            >
              <Lock size={20} />
              Changer mot de passe
            </button>
            <button
              onClick={handleLogout}
              className="admin-nav-item w-full text-red-300 hover:text-red-200 hover:bg-red-500/20"
              data-testid="admin-logout"
            >
              <LogOut size={20} />
              Deconnexion
            </button>
          </div>
        </div>
      </aside>

      {/* Password change modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center px-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-[#003366]">Changer le mot de passe</h3>
              <button onClick={() => setShowPasswordModal(false)}><X size={20} /></button>
            </div>
            <form onSubmit={handlePasswordChange} className="space-y-4">
              <Input type="password" placeholder="Mot de passe actuel" value={pwdForm.current_password} onChange={(e) => setPwdForm({ ...pwdForm, current_password: e.target.value })} required data-testid="current-pwd-input" />
              <Input type="password" placeholder="Nouveau mot de passe (min. 8 car.)" value={pwdForm.new_password} onChange={(e) => setPwdForm({ ...pwdForm, new_password: e.target.value })} required data-testid="new-pwd-input" />
              <Button type="submit" disabled={pwdLoading} className="w-full bg-[#003366] rounded-full" data-testid="save-pwd-btn">
                {pwdLoading ? 'Enregistrement...' : 'Enregistrer'}
              </Button>
            </form>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Header */}
        <header className="bg-white shadow-sm sticky top-0 z-40">
          <div className="flex items-center justify-between px-4 py-4 lg:px-8">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100"
            >
              {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
            
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-500">Admin CEFAT</span>
              <div className="w-10 h-10 bg-[#003366] rounded-full flex items-center justify-center text-white font-bold">
                A
              </div>
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 p-4 lg:p-8">
          {children}
        </main>
      </div>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAnalytics = useCallback(async () => {
    try {
      const response = await authFetch(`${API_URL}/analytics`);
      if (response.status === 401) { navigate('/admin'); return; }
      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    const user = getCurrentUser();
    if (!user.role) { navigate('/admin'); return; }
    fetchAnalytics();
  }, [fetchAnalytics, navigate]);

  const handleExport = async () => {
    try {
      const response = await authFetch(`${API_URL}/analytics/export?format=csv`);
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'preregistrations.csv';
        a.click();
        toast.success('Export réussi');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'export');
    }
  };

  const handlePurge = async () => {
    if (!window.confirm('ATTENTION : Cette action va supprimer TOUTES les donnees (preinscriptions, rappels, messages, blogs, medias, analytics). Cette action est irreversible. Continuer ?')) return;
    if (!window.confirm('Etes-vous vraiment sur ? Toutes les donnees seront perdues definitivement.')) return;
    
    try {
      const response = await authFetch(`${API_URL}/admin/purge-data`, {
        method: 'DELETE',
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        fetchAnalytics();
      } else {
        toast.error('Erreur lors de la purge');
      }
    } catch (error) {
      toast.error('Erreur lors de la purge');
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await authFetch(`${API_URL}/reports/pdf`);
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `rapport_cefat_${new Date().toISOString().slice(0,7)}.pdf`;
        a.click();
        toast.success('Rapport PDF telecharge');
      } else {
        toast.error('Erreur lors du telechargement');
      }
    } catch {
      toast.error('Erreur lors du telechargement');
    }
  };

  const centerData = analytics?.by_center 
    ? Object.entries(analytics.by_center).map(([name, value]) => ({ name: centerNames[name] || name, value }))
    : [];

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#003366] border-t-transparent"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div data-testid="admin-dashboard">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold text-[#003366]">Tableau de bord</h1>
            <p className="text-gray-500 mt-1">Vue d'ensemble des performances</p>
          </div>
          <div className="flex gap-3 mt-4 md:mt-0">
            <Button onClick={handleDownloadPDF} variant="outline" className="border-[#003366] text-[#003366] hover:bg-[#003366]/5 rounded-full" data-testid="pdf-btn">
              <FileDown size={18} className="mr-2" /> Rapport PDF
            </Button>
            <Button onClick={handleExport} className="bg-[#003366] hover:bg-[#002244] rounded-full" data-testid="export-btn">
              <Download size={18} className="mr-2" /> Exporter CSV
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="admin-card"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total préinscriptions</p>
                <p className="font-heading text-3xl font-bold text-[#003366]">{analytics?.total_preregistrations || 0}</p>
              </div>
              <div className="w-12 h-12 bg-[#003366]/10 rounded-lg flex items-center justify-center">
                <Users className="text-[#003366]" size={24} />
              </div>
            </div>
            <div className="mt-4 flex items-center gap-2 text-sm">
              <TrendingUp className="text-green-600" size={16} />
              <span className="text-green-600 font-medium">+12%</span>
              <span className="text-gray-500">ce mois</span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="admin-card"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">En attente</p>
                <p className="font-heading text-3xl font-bold text-yellow-600">{analytics?.pending_count || 0}</p>
              </div>
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                <Clock className="text-yellow-600" size={24} />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="admin-card"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Acceptées</p>
                <p className="font-heading text-3xl font-bold text-green-600">{analytics?.accepted_count || 0}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="text-green-600" size={24} />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="admin-card"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Vues blog</p>
                <p className="font-heading text-3xl font-bold text-[#8B5CF6]">{analytics?.total_blog_views || 0}</p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <Eye className="text-[#8B5CF6]" size={24} />
              </div>
            </div>
          </motion.div>
        </div>

        {/* Charts */}
        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          {/* Monthly Trend */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="admin-card"
          >
            <h2 className="font-heading text-xl font-bold text-[#003366] mb-6">Tendance mensuelle</h2>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={analytics?.monthly_trend || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Area 
                    type="monotone" 
                    dataKey="count" 
                    stroke="#003366" 
                    fill="#003366" 
                    fillOpacity={0.2}
                    name="Préinscriptions"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </motion.div>

          {/* By Center */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="admin-card"
          >
            <h2 className="font-heading text-xl font-bold text-[#003366] mb-6">Par centre</h2>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={centerData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {centerData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </motion.div>
        </div>

        {/* Recent Registrations */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="admin-card"
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading text-xl font-bold text-[#003366]">Dernières préinscriptions</h2>
            <Link to="/admin/preregistrations" className="text-[#DC143C] hover:underline text-sm font-medium flex items-center">
              Voir tout <ChevronRight size={16} />
            </Link>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left border-b">
                  <th className="pb-3 text-sm font-semibold text-gray-500">Nom</th>
                  <th className="pb-3 text-sm font-semibold text-gray-500">Centre</th>
                  <th className="pb-3 text-sm font-semibold text-gray-500">Programme</th>
                  <th className="pb-3 text-sm font-semibold text-gray-500">Date</th>
                </tr>
              </thead>
              <tbody>
                {analytics?.recent_registrations?.slice(0, 5).map((reg, index) => (
                  <tr key={index} className="border-b last:border-0">
                    <td className="py-4 font-medium text-[#003366]">{reg.name}</td>
                    <td className="py-4">
                      <span className="badge badge-blue">{centerNames[reg.center] || reg.center}</span>
                    </td>
                    <td className="py-4 text-gray-600">{reg.program}</td>
                    <td className="py-4 text-gray-500 text-sm">{formatDate(reg.created_at)}</td>
                  </tr>
                ))}
                {(!analytics?.recent_registrations || analytics.recent_registrations.length === 0) && (
                  <tr>
                    <td colSpan={4} className="py-8 text-center text-gray-500">
                      Aucune préinscription récente
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </motion.div>
      </div>
    </AdminLayout>
  );
}
